import { useState, useEffect, useCallback, useRef } from 'react';
import Section1Hero from './Section1Hero';
import Section2Achievement from './Section2Achievement';
import Section3Questions from './Section3Questions';
import Section4Answers from './Section4Answers';
import Section5Flow from './Section5Flow';
import Section6DualView from './Section6DualView';
import Section7Roadmap from './Section7Roadmap';
import Section8ReportPreview from './Section8ReportPreview';

const TOTAL_SLIDES = 8;

export default function Section1Carousel() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev' | 'initial'>('initial');
  const containerRef = useRef<HTMLDivElement>(null);

  const goNext = useCallback(() => {
    setDirection('next');
    setActiveSlide((prev) => (prev < TOTAL_SLIDES - 1 ? prev + 1 : prev));
  }, []);

  const goPrev = useCallback(() => {
    setDirection('prev');
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goTo = useCallback((i: number) => {
    setDirection(i > activeSlide ? 'next' : i < activeSlide ? 'prev' : 'initial');
    setActiveSlide(i);
  }, [activeSlide]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        goNext();
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev]);

  const slides = [
    <Section1Hero forceVisible key="p1" />,
    <Section2Achievement forceVisible key="p2" />,
    <Section3Questions forceVisible key="p3" />,
    <Section4Answers forceVisible key="p4" />,
    <Section5Flow forceVisible key="p5" />,
    <Section6DualView forceVisible key="p6" />,
    <Section7Roadmap forceVisible key="p7" />,
    <Section8ReportPreview forceVisible key="p8" />,
  ];

  return (
    <section
      id="section-1"
      ref={containerRef}
      className="relative min-h-screen overflow-hidden bg-background-50"
    >
      {/* 浮动光斑 */}
      <div className="absolute top-1/4 left-[8%] w-64 h-64 rounded-full bg-primary-100/6 blur-[90px] pointer-events-none animate-orb-float-1" />
      <div className="absolute top-2/3 right-[12%] w-56 h-56 rounded-full bg-accent-100/5 blur-[80px] pointer-events-none animate-orb-float-2" />
      <div className="absolute top-1/2 left-[55%] w-48 h-48 rounded-full bg-secondary-100/4 blur-[70px] pointer-events-none animate-orb-float-3" />

      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-all duration-600 ${
            activeSlide === i
              ? direction === 'next'
                ? 'animate-slide-in-right z-10 pointer-events-auto'
                : direction === 'prev'
                  ? 'animate-slide-in-left z-10 pointer-events-auto'
                  : 'animate-slide-initial z-10 pointer-events-auto'
              : 'opacity-0 z-0 pointer-events-none scale-[0.98]'
          }`}
        >
          {slide}
        </div>
      ))}

      {/* Left Arrow */}
      <button
        onClick={goPrev}
        disabled={activeSlide === 0}
        className={`absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          activeSlide === 0
            ? 'glass border-white/40 text-foreground-950 cursor-default'
            : 'glass border-white/60 text-gold-500 hover:border-gold-300/60 hover:scale-110 active:scale-95'
        }`}
      >
        <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
      </button>

      {/* Right Arrow */}
      <button
        onClick={goNext}
        disabled={activeSlide === TOTAL_SLIDES - 1}
        className={`absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          activeSlide === TOTAL_SLIDES - 1
            ? 'glass border-white/40 text-foreground-950 cursor-default'
            : 'glass border-white/60 text-gold-500 hover:border-gold-300/60 hover:scale-110 active:scale-95'
        }`}
      >
        <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
      </button>

      {/* Slide indicator dots */}
      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
        {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-300 cursor-pointer ${
              activeSlide === i
                ? 'w-8 h-2.5 bg-gold-500 animate-bounce-in'
                : 'w-2.5 h-2.5 bg-background-300/50 hover:bg-gold-300/60 hover:scale-125'
            }`}
          />
        ))}
      </div>

      {/* Slide counter */}
      <div className="absolute bottom-6 md:bottom-8 right-6 md:right-10 z-30">
        <span className="font-mono text-xs text-gold-500 tracking-wider">
          {activeSlide + 1} / {TOTAL_SLIDES}
        </span>
      </div>
    </section>
  );
}